import { Injectable } from '@angular/core';
import { Device, DeviceInfo } from '@core/capacitor-plugins/capacitor-plugins.class';
import { SunmiDeviceModel, SunmiPrinterWidth } from './sunmi.service.model';
import { SunmiScannerType } from '@core/capacitor-plugins/capacitor-plugins.class';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { StorageKeys } from '@shared/constants';
import { Platform } from '@ionic/angular';

@Injectable()
export abstract class ISunmiService {
  abstract get isSunmiDevice(): boolean;
  abstract get sunmiDeviceModel(): SunmiDeviceModel;
  abstract get hasPrinter(): boolean;
  /** Intent-based scanner via com.sunmi.sunmiqrcodescanner — older firmware only (V2s/V2sPlus). */
  abstract get hasAppScanner(): boolean;
  /** AIDL built-in scanner — D3 Mini and devices on newer Sunmi firmware. */
  abstract get hasInnerScanner(): boolean;
  /** USB/external scanner via broadcast — requires USB-connected scanner device. */
  abstract get hasBroadcastScanner(): boolean;
  /** Best SunmiScannerType to use for the current device. Prefer this over manual checks. */
  abstract get recommendedScannerType(): SunmiScannerType;
  abstract get printerWidth(): SunmiPrinterWidth;
  abstract get printerModel(): SunmiPrinterWidth;
}

@Injectable()
export class SunmiService implements ISunmiService {
  private _isSunmiDevice: boolean = false;
  private _sunmiDeviceModel: SunmiDeviceModel = SunmiDeviceModel.Unknown;
  private _initPromise?: Promise<void>;

  constructor(
    private platform: Platform,
    private localStorage: LocalStorage
  ) {
    void this.init();
  }

  private init(): Promise<void> {
    if (!this._initPromise) {
      this._initPromise = this.runSunmiDevice();
    }
    return this._initPromise;
  }

  private async runSunmiDevice(): Promise<void> {
    await this.platform.ready();
    // Use .catch(() => null) to suppress the rejection at the Promise level so that
    // Sentry's zone.js integration does not capture "plugin is not implemented" errors
    // (zone hooks fire before a try/catch block can swallow the rejection).
    const info: DeviceInfo | null = await Device.getInfo().catch(() => null);
    if (info) {
      const manufacturer: string = (info.manufacturer || '').toLowerCase();
      this._isSunmiDevice = manufacturer === 'sunmi';
      this._sunmiDeviceModel = this.detectSunmiModel(info);
    }
  }

  private detectSunmiModel(info: DeviceInfo): SunmiDeviceModel {
    let model: string = info?.model || '';
    model = model.toLocaleLowerCase();

    if (model.includes('v2splus')) return SunmiDeviceModel.V2sPlus;
    if (model.includes('v2s')) return SunmiDeviceModel.V2s;
    if (model.includes('t2s')) return SunmiDeviceModel.T2s;
    if (model.includes('d3mini')) return SunmiDeviceModel.D3Mini;
    if (model.includes('d3 mini')) return SunmiDeviceModel.D3Mini;
    if (model.includes('t3')) return SunmiDeviceModel.T3;
    if (model.includes('v3_mix')) return SunmiDeviceModel.V3mix;

    return SunmiDeviceModel.Unknown;
  }

  get isSunmiDevice() {
    return this._isSunmiDevice;
  }

  get sunmiDeviceModel() {
    return this._sunmiDeviceModel;
  }

  get hasPrinter() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.D3Mini:
      case SunmiDeviceModel.T3:
      case SunmiDeviceModel.V3mix:
        return true;
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  /**
   * Only older devices (V2s/V2sPlus) have com.sunmi.sunmiqrcodescanner installed.
   * D3 Mini and newer firmware devices do NOT have this app — use InnerScanner instead.
   */
  get hasAppScanner() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
        return true;
      case SunmiDeviceModel.D3Mini: // FIX: app removed from firmware, was incorrectly true
      case SunmiDeviceModel.T3: // newer firmware — uses AIDL InnerScanner
      case SunmiDeviceModel.V3mix: // newer firmware — uses AIDL InnerScanner
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  /**
   * AIDL-based built-in scanner service (com.sunmi.scanner.IInnerScanner).
   * Available on D3 Mini and devices running newer Sunmi firmware (Android 11+).
   */
  get hasInnerScanner() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.D3Mini:
      case SunmiDeviceModel.T3:
      case SunmiDeviceModel.V3mix:
        return true;
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  /**
   * USB/external scanner via com.sunmi.scanner.ACTION_DATA_CODE_RECEIVED broadcast.
   * D3 Mini uses AIDL for its built-in scanner — Broadcast mode is for USB scanners only.
   */
  get hasBroadcastScanner() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.T3:
      case SunmiDeviceModel.V3mix:
        return true;
      case SunmiDeviceModel.D3Mini:
      case SunmiDeviceModel.Unknown:
        return false;
    }
  }

  /**
   * Returns the best SunmiScannerType for the current device.
   * Use this instead of manually checking hasAppScanner / hasInnerScanner.
   *
   * Usage:
   *   sunmiScannerPlugin.startScanner(sunmiService.recommendedScannerType)
   */
  get recommendedScannerType(): SunmiScannerType {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.V2s:
      case SunmiDeviceModel.V2sPlus:
        return SunmiScannerType.App;
      case SunmiDeviceModel.D3Mini:
        return SunmiScannerType.InnerScanner;
      case SunmiDeviceModel.T3:
      case SunmiDeviceModel.V3mix:
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.Unknown:
      default:
        return SunmiScannerType.Auto;
    }
  }

  get printerWidth() {
    switch (this._sunmiDeviceModel) {
      case SunmiDeviceModel.T2s:
      case SunmiDeviceModel.T3:
        return SunmiPrinterWidth.w80mm;
      case SunmiDeviceModel.V2s:
        return SunmiPrinterWidth.w40mm;
      case SunmiDeviceModel.D3Mini:
      case SunmiDeviceModel.V3mix:
        return this.printerModel;
      default:
        return SunmiPrinterWidth.w58mm;
    }
  }

  get printerModel(): SunmiPrinterWidth {
    const rawModel: string = this.localStorage.getObject(StorageKeys.sunmiPrinterInfo)?.model;

    if (!rawModel) {
      return SunmiPrinterWidth.w80mm;
    }

    const model: string = rawModel?.replace(/\s+/g, '').toLowerCase();

    return model.includes('80') ? SunmiPrinterWidth.w80mm : SunmiPrinterWidth.w58mm;
  }
}
